import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import React, { useContext, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Global from '../../../../../store/global-selectors';
import { StoreDropdown } from './store-dropdown';
import { Button } from 'ui-components/components/button';
import { Input } from 'ui-components/components/input';
import AsModalWithContext from '../../../../../components/as-modal/with-context';
import { DeletionWarningModal } from './deletion-warning-modal';
import { ProductAliasPropType } from './prop-types/product-alias';
import { ProductDetailsContext } from '../../../context';
import { useIntl } from 'react-intl';
import { messages } from './messages';

import style from './style.module.less';

export const Alias = ({ alias, onDeleteAlias }) => {
  const intl = useIntl();
  const { setProductAliases } = useContext(ProductDetailsContext);
  const { aliasSKU, productAliasId, storeGuid, isNewAlias } = alias;
  const stores = useSelector(Global.selectSellerStores) || [];
  const selectedStore = stores.find((store) => store.storeGuid === storeGuid);
  const onConfirmDeletion = useCallback(() => {
    onDeleteAlias(productAliasId);
  }, [onDeleteAlias, productAliasId]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const onCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const confirmDeletionIfNeeded = useCallback(() => {
    if (!isNewAlias) {
      setIsModalOpen(true);
      return;
    }
    onDeleteAlias(productAliasId);
  }, [setIsModalOpen, isNewAlias, onDeleteAlias, productAliasId]);

  const updateAlias = useCallback(
    (newAlias) => {
      setProductAliases((prevAliases) => {
        const index = prevAliases.findIndex((a) => a.productAliasId === newAlias.productAliasId);
        return index !== -1 ? [...prevAliases.slice(0, index), newAlias, ...prevAliases.slice(index + 1)] : [...prevAliases, newAlias];
      });
    },
    [setProductAliases],
  );

  const setNewAliasSku = useCallback(
    (sku) => {
      const newAlias = {
        ...alias,
        aliasSKU: sku,
      };
      updateAlias(newAlias);
    },
    [alias, updateAlias],
  );

  const onAliasStoreChange = useCallback(
    (selectedStoreGuid) => {
      const newAlias = {
        ...alias,
        storeGuid: selectedStoreGuid,
      };
      updateAlias(newAlias);
    },
    [alias, updateAlias],
  );

  return (
    <div className={style['store-alias']}>
      {!isNewAlias && <div>{aliasSKU}</div>}
      {isNewAlias && <Input className={style['alias-input']} onChange={setNewAliasSku} value={aliasSKU} />}
      <div className={style['store-selector']}>
        <StoreDropdown onChange={onAliasStoreChange} selectedStoreId={storeGuid} />
      </div>
      <div className={style['alias-delete']}>
        <Button className={style['delete-item']} onClick={confirmDeletionIfNeeded} theme='unstyled'>
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </div>
      {isModalOpen && (
        <AsModalWithContext Context={ProductDetailsContext}>
          <DeletionWarningModal
            aliasSku={aliasSKU}
            aliasStore={selectedStore ? selectedStore.name : intl.formatMessage(messages.anyStore)}
            onCloseModal={onCloseModal}
            onConfirmDeletion={onConfirmDeletion}
          />
        </AsModalWithContext>
      )}
    </div>
  );
};

Alias.propTypes = {
  alias: ProductAliasPropType.isRequired,
  onDeleteAlias: PropTypes.func.isRequired,
};
